import { ProposalWithOwner, BenchmarkThreshold } from "@/utils/types";
import { annualizeProposalCashValue, convertIncomeFrequency } from "lib/shared/compensation/cash";
import { EmploymentSubType } from "lib/shared/compensation/employment/types";
import { ProposalWithOwnerAndRange, Proposal } from "lib/shared/proposals/types";
import { Review, SalaryIncomeFrequency, User } from "prisma/cm/client";
import { calculateSalaryBand } from "utils/helpers";

import { FieldProposal } from "../fields/types";

export const INCOME_FREQUENCIES_WEIGHTED = [
  SalaryIncomeFrequency.HOURLY,
  SalaryIncomeFrequency.DAILY,
  SalaryIncomeFrequency.WEEKLY,
  SalaryIncomeFrequency.BI_WEEKLY,
  SalaryIncomeFrequency.MONTHLY,
  SalaryIncomeFrequency.SEMI_MONTHLY,
  SalaryIncomeFrequency.YEARLY,
  SalaryIncomeFrequency.FIXED,
];

/**
 * Convenience method to get both proposed pay frequency and employment subtype.
 */
export function getProposedIncomeFrequencyAndEmploymentSubType({
  proposalWithOwner,
}: {
  proposalWithOwner: ProposalWithOwner;
}) {
  return {
    proposedSalaryIncomeFrequency: getInCycleSalaryIncomeFrequency({ proposalWithOwner }),
    proposedEmploymentSubType: getInCycleEmploymentSubType({ proposalWithOwner }),
  };
}

/**
 * Get a user's pay frequency for use in-cycle. If a user's pay frequency is changing
 * as part of the cycle, this will return the new pay frequency.  Otherwise, it will return
 * the user's current pay frequency.
 */
export function getInCycleSalaryIncomeFrequency({
  proposalWithOwner,
}: {
  proposalWithOwner: ProposalWithOwner;
}) {
  return (
    proposalWithOwner.salaryIncomeFrequency ??
    proposalWithOwner.owner?.salaryIncomeFrequency ??
    SalaryIncomeFrequency.YEARLY
  );
}

/**
 * Returns a user's range's mid-point adjusted for possible pay-frequency change.
 */
export function getInCycleSalaryBand({
  review,
  proposal,
  threshold,
}: {
  review: Review;
  proposal: ProposalWithOwnerAndRange;
  threshold: BenchmarkThreshold;
}) {
  const rangeIncomeFrequency = proposal.range
    ? proposal.range?.salaryIncomeFrequency
    : proposal.owner?.range?.salaryIncomeFrequency ?? SalaryIncomeFrequency.YEARLY;

  let rangeMid = calculateSalaryBand(proposal as Proposal, review, threshold);

  const existingSalaryIncomeFrequency =
    proposal.owner.salaryIncomeFrequency ?? SalaryIncomeFrequency.YEARLY;
  const proposedSalaryIncomeFrequency = getInCycleSalaryIncomeFrequency({
    proposalWithOwner: proposal,
  });
  const proposedEmploymentSubType = getInCycleEmploymentSubType({ proposalWithOwner: proposal });

  const requiresFrequencyConversion =
    proposedSalaryIncomeFrequency !== existingSalaryIncomeFrequency;

  if (requiresFrequencyConversion) {
    rangeMid = convertAmountForChangedPayFrequency({
      amount: rangeMid,
      oldPayFrequency: rangeIncomeFrequency,
      newPayFrequency: proposedSalaryIncomeFrequency,
      employmentSubType: proposedEmploymentSubType,
    });
  }

  return rangeMid;
}

/**
 * Returns a user's employment subtype adjusted for possible pay-frequency change.
 */
export function getInCycleEmploymentSubType({
  proposalWithOwner,
}: {
  proposalWithOwner: ProposalWithOwner;
}) {
  if (!proposalWithOwner.salaryIncomeFrequency)
    return (proposalWithOwner.owner?.employmentSubType as EmploymentSubType) ?? "full_time";

  const salaryIncomeFrequency = getInCycleSalaryIncomeFrequency({ proposalWithOwner });

  return salaryIncomeFrequency === "YEARLY"
    ? "full_time"
    : (proposalWithOwner.owner.employmentSubType as EmploymentSubType);
}

/**
 * Returns user salary adjusted for possible pay-frequency change.
 */
export function getUserSalaryForInCycleCalculations({
  proposalWithOwner,
}: {
  proposalWithOwner: ProposalWithOwner;
}) {
  if (!proposalWithOwner.salaryIncomeFrequency) return proposalWithOwner.owner?.salary ?? 0;

  const salaryIncomeFrequency = getInCycleSalaryIncomeFrequency({ proposalWithOwner });
  const employmentSubType = getInCycleEmploymentSubType({ proposalWithOwner });

  return convertAmountForChangedPayFrequency({
    amount: proposalWithOwner.owner?.salary ?? 0,
    oldPayFrequency: proposalWithOwner.owner?.salaryIncomeFrequency ?? SalaryIncomeFrequency.YEARLY,
    newPayFrequency: salaryIncomeFrequency,
    // COM-5421 - if salary income frequency is yearly (possibly due to change from hourly to yearly)
    // assume that employment subtype is full-time.  Otherwise, default to user.
    employmentSubType: employmentSubType,
  });
}

/**
 * Get a user's salary converted to a specified pay-frequency.
 */
export function getUserSalaryConvertedToNewPayFrequency({
  user,
  newIncomeFrequency,
  newEmploymentSubType,
}: {
  user: User;
  newIncomeFrequency: SalaryIncomeFrequency;
  newEmploymentSubType: EmploymentSubType;
}) {
  const base = user.salary ?? 0;

  return convertAmountForChangedPayFrequency({
    amount: base,
    oldPayFrequency: user.salaryIncomeFrequency ?? SalaryIncomeFrequency.YEARLY,
    newPayFrequency: newIncomeFrequency,
    employmentSubType: newEmploymentSubType,
  });
}

export function convertAmountForChangedPayFrequency({
  amount,
  oldPayFrequency,
  newPayFrequency,
  employmentSubType,
}: {
  amount: number;
  oldPayFrequency: SalaryIncomeFrequency;
  newPayFrequency: SalaryIncomeFrequency;
  employmentSubType: EmploymentSubType;
}) {
  return convertIncomeFrequency(amount, oldPayFrequency, newPayFrequency, employmentSubType);
}

export function getFormattedPayFrequencyLabel(frequency: SalaryIncomeFrequency) {
  switch (frequency) {
    case SalaryIncomeFrequency.HOURLY:
      return "hr";
    case SalaryIncomeFrequency.DAILY:
      return "day";
    case SalaryIncomeFrequency.WEEKLY:
      return "wk";
    case SalaryIncomeFrequency.BI_WEEKLY:
      return "bi-wk";
    case SalaryIncomeFrequency.MONTHLY:
      return "mo";
    case SalaryIncomeFrequency.SEMI_MONTHLY:
      return "semi-mo";
    case SalaryIncomeFrequency.YEARLY:
      return "yr";
  }

  return frequency;
}
export const getAnnualizedProposedSalary = (proposal: FieldProposal) => {
  const newSalary =
    getUserSalaryForInCycleCalculations({ proposalWithOwner: proposal }) + (proposal.raise ?? 0);

  return annualizeProposalCashValue({
    value: newSalary,
    proposalWithOwner: proposal,
  });
};
